import { useMemo } from 'react';
import { CHAINFLIP_BLOCK_TIME_SECONDS } from '@chainflip/utils/consts';
import { flip$ } from '@/shared/assets/tokens';
import { getLatestAuctionQuery } from '@/shared/graphql/cache/auction';
import { getLatestEpochsQuery } from '@/shared/graphql/explorer/queries';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import useTokenPrice from '@/shared/hooks/useTokenPrice';
import FlipAmount from '@/shared/utils/FlipAmount';
import TokenAmount from '@/shared/utils/TokenAmount';

export type AuctionInfo = {
  percentageElapsed: number;
  epochLengthBlocks: number;
  startBlockNumber: number;
  endBlockNumber: number;
  currentHeight: number;
  startsAt: Date;
  endsAt: Date;
  hasEnded: boolean;
  minActiveBid: TokenAmount | undefined;
  minBid: TokenAmount;
  blockReward: TokenAmount;
  minActiveBidUSDC: string | undefined;
  isRedemptionPeriod: boolean;
  redemptionPeriodAsPercentage: number;
  targetSetSize: number;
  authorityRotationEnabled: boolean;
  delegation: {
    amount: TokenAmount;
    valueUsd: string;
  };
  totalBondedFlip: TokenAmount;
  totalBondedFlipValueUsd: string;
};

type AuctionHook = {
  auction: AuctionInfo | null;
  isLoading: boolean;
  error?: Error;
  initialized: boolean;
};

export const calculateEndTime = ({
  endBlockNumber,
  currentHeight,
}: {
  endBlockNumber: number;
  currentHeight: number;
}): Date => {
  const blocksRemaining = endBlockNumber - currentHeight;

  return new Date(blocksRemaining * (CHAINFLIP_BLOCK_TIME_SECONDS * 1000) + Date.now());
};

export default function useAuction(options?: { pollInterval?: number }): AuctionHook {
  const { price: flipUsdcPrice = 0 } = useTokenPrice(flip$);
  const result = useGqlQuery(getLatestAuctionQuery, {
    refetchInterval: options?.pollInterval,
    context: { clientName: 'statechainCache' },
  });
  const epochResult = useGqlQuery(getLatestEpochsQuery, {
    variables: {
      // only the latest epoch is read, but matching useEpochsWithRotation lets both share one request
      limit: 2,
    },
    refetchInterval: options?.pollInterval,
    context: { clientName: 'processor' },
  });

  return useMemo(() => {
    let auctionInfo = null;

    const { data, isLoading, error } = result;
    const { data: epochData, isLoading: isEpochLoading, error: epochError } = epochResult;

    if (data?.auction) {
      const {
        endBlockNumber,
        currentHeight,
        startBlockNumber,
        redemptionPeriodAsPercentage,
        targetSetSize,
        authorityRotationEnabled,
        ...auction
      } = data.auction;

      const auctionStartBlock = Math.round((startBlockNumber + endBlockNumber) / 2);
      const blocksToStart = auctionStartBlock - currentHeight;

      const blocksRemaining = endBlockNumber - currentHeight;
      const auctionHasEnded = blocksRemaining <= 0;

      const epochLengthBlocks = endBlockNumber - startBlockNumber;
      const percentageElapsed = auctionHasEnded
        ? 100
        : (1 - blocksRemaining / epochLengthBlocks) * 100;
      const isRedemptionPeriod = percentageElapsed < redemptionPeriodAsPercentage;

      const minActiveBid =
        auction.minActiveBid == null ? undefined : new FlipAmount(auction.minActiveBid);

      const minActiveBidUSDC = minActiveBid?.toBigNumber().multipliedBy(flipUsdcPrice);

      const totalDelegatedFlipAmount = TokenAmount.fromAsset(
        data.delegations?.aggregates?.sum?.upcomingAmount ?? 0,
        'Flip',
      );
      const totalDelegatedFlipAmountUSDC = totalDelegatedFlipAmount
        .toBigNumber()
        .multipliedBy(flipUsdcPrice);

      const totalBondedFlip = TokenAmount.fromAsset(
        epochData?.latestEpoch?.nodes.at(0)?.totalBonded ?? 0,
        'Flip',
      );
      const totalBondedFlipValueUsd = totalBondedFlip.toBigNumber().multipliedBy(flipUsdcPrice);

      auctionInfo = {
        percentageElapsed,
        epochLengthBlocks,
        startBlockNumber,
        endBlockNumber,
        currentHeight,
        startsAt: new Date(blocksToStart * (CHAINFLIP_BLOCK_TIME_SECONDS * 1000) + Date.now()),
        endsAt: calculateEndTime({ endBlockNumber, currentHeight }),
        hasEnded: auctionHasEnded,
        minActiveBid,
        minBid: new FlipAmount(auction.minBid),
        blockReward: new FlipAmount(auction.blockReward),
        minActiveBidUSDC: minActiveBidUSDC && minActiveBidUSDC.toString(),
        isRedemptionPeriod,
        redemptionPeriodAsPercentage,
        targetSetSize,
        authorityRotationEnabled,
        delegation: {
          amount: totalDelegatedFlipAmount,
          valueUsd: totalDelegatedFlipAmountUSDC.toString(),
        },
        totalBondedFlip,
        totalBondedFlipValueUsd: totalBondedFlipValueUsd.toString(),
      };
    }

    return {
      auction: auctionInfo,
      isLoading: isLoading || isEpochLoading,
      error: error ?? epochError ?? undefined,
      initialized: Boolean(auctionInfo),
    };
  }, [result, flipUsdcPrice, epochResult]);
}
